import React from 'react'

interface ImpressionUnavailableProps {
    unavailable: () => void
    available: () => void
    /** 50 for element to be 50% visible in viewport */
    percentageUnviewable?: number
    debug?: debug.Debugger
}

/**
 * Similar to "ImpressionAvailable", however different in the sense of INSTEAD OF waiting for an item to become
 * visible, and firing an unavailable function and closing/ending the observer, we WAIT for the item to become
 * unavailable (based on the percentageUnviewable prop) and then fire the unavailable function, while maintaining
 * the observer to wait for it to become available again, and fire the available function accordingly.
 */
export const useImpressionUnavailable = ({
    unavailable,
    available,
    debug,
    percentageUnviewable = 50,
}: ImpressionUnavailableProps) => {
    const lazyLoadRef = React.useRef<HTMLDivElement | null>(null)
    const refUnavailable = React.useRef<boolean>(false)

    React.useEffect(() => {
        debugImpression(
            're-rendering ImpressionUnavailable element:',
            debug,
            percentageUnviewable,
            available,
            unavailable,
        )

        if (!lazyLoadRef.current) {
            debugImpression(
                'element not yet available or still loading, skipping setup',
                debug,
            )
            return
        }

        // Calculate the threshold as a percentage, means we dont have to update all the consumers to use a decimal value \o/
        const threshold = Math.min(Math.max(percentageUnviewable / 100, 0), 1)
        const observer = new IntersectionObserver(
            ([entry]) => {
                // In other words, if the threshold is met.
                if (entry.isIntersecting) {
                    // If it's currently unavailable, fire an unavailable function
                    if (!refUnavailable.current) {
                        debugImpression(
                            'Firing available function, as entered from unavailable state.',
                            debug,
                        )
                        setTimeout(available)
                    }
                    refUnavailable.current = true
                } else {
                    // If it's currently available, fire an available function
                    if (refUnavailable.current) {
                        debugImpression(
                            'Firing unavailable function, as entered from available state.',
                            debug,
                        )
                        setTimeout(unavailable)
                    }
                    refUnavailable.current = false
                }
            },
            {
                threshold: [threshold],
            },
        )
        observer.observe(lazyLoadRef.current)

        return () => {
            debugImpression('useEffect ending, removing listeners', debug)
            observer.disconnect()
        }
    }, [available, unavailable, debug, percentageUnviewable])

    return lazyLoadRef
}

export const ImpressionUnavailable: React.FC<
    ImpressionUnavailableProps & {
        children: (ref: React.RefObject<any>) => React.ReactNode
    }
> = (props) => {
    const lazyLoadRef = useImpressionUnavailable(props)
    return props.children(lazyLoadRef) as any
}

// -------------- HELPERS --------------

const debugImpression = (
    message: string,
    debug?: debug.Debugger,
    ...args: any[]
) => {
    if (debug !== undefined) {
        debug(message, args)
    }
}
